/**
 * Shared form validators.
 *
 * Every validator returns an error message string, or null when the value is
 * acceptable. That shape lets callers build a `{ field: message | null }` map and
 * hand each message straight to TextInput's `error` prop.
 *
 * The password rules here mirror Django's AUTH_PASSWORD_VALIDATORS (see
 * server/project/settings.py) so the client rejects what the server would reject.
 * The server remains the real gate — CommonPasswordValidator and
 * UserAttributeSimilarityValidator can only run there, so a password that passes
 * here can still come back rejected, and the caller must surface that.
 */

// Matches Django's MinimumLengthValidator default.
export const PASSWORD_MIN_LENGTH = 8;

export function validateRequired(value: string, fieldLabel: string): string | null {
  if (!value || value.trim() === "") return `${fieldLabel} is required`;
  return null;
}

export function validateEmail(email: string): string | null {
  if (!email || email.trim() === "") return "Email is required";
  // Deliberately permissive: the aim is to catch typos like a missing "@", not to
  // adjudicate RFC 5322. The server's EmailField is the authority.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
    return "Please enter a valid email address";
  }
  return null;
}

export function validatePassword(password: string): string | null {
  if (!password) return "Password is required";
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters`;
  }
  // Mirrors Django's NumericPasswordValidator.
  if (/^\d+$/.test(password)) {
    return "Password cannot be entirely numeric";
  }
  return null;
}

export function validatePasswordConfirmation(
  password: string,
  confirmPassword: string
): string | null {
  if (!confirmPassword) return "Please confirm your password";
  if (password !== confirmPassword) return "Passwords do not match";
  return null;
}

/** True when every value in an error map is null. */
export function isValid(errors: Record<string, string | null>): boolean {
  return Object.values(errors).every((error) => error === null);
}

/**
 * Read one field's error out of a DRF error body.
 *
 * DRF reports serializer errors as `{ field: ["message", ...] }`, but the auth
 * views also return bare strings for some fields. Returns null when the field is
 * absent, so the result drops straight into an error map.
 */
export function fieldError(data: any, field: string): string | null {
  const value = data?.[field];
  if (typeof value === "string") return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return null;
}

/**
 * Pull a readable message out of a DRF error response.
 *
 * DRF serializer errors arrive as `{ field: ["message", ...] }`, but our endpoints
 * also return `{ message: "..." }`, `{ detail: "..." }` and plain strings, a thunk
 * that used rejectWithValue may hand back a bare string, and an axios error keeps
 * the body under `.response.data`. Falls back to `fallback` so callers never render
 * "[object Object]".
 */
export function extractApiError(payload: any, fallback: string): string {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;

  const data = payload.response?.data ?? payload.data ?? payload;
  if (typeof data === "string") return data;

  // Field errors beat the top-level message: ingestion reports a rejected submission
  // as `{message: "Invalid request", errors: {text: ["..."]}}`, where only `errors`
  // says why.
  const fieldErrors = data?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const messages = Object.values(fieldErrors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === "string" && value.length > 0);
    if (messages.length > 0) return messages.join(" ");
  }

  if (typeof data?.message === "string") return data.message;
  if (typeof data?.detail === "string") return data.detail;

  if (typeof data === "object") {
    for (const value of Object.values(data)) {
      if (typeof value === "string") return value;
      if (Array.isArray(value) && typeof value[0] === "string") return value[0];
    }
  }
  return fallback;
}
